import { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import LiquidBackground from "./LiquidBackground";
import FluidCanvas from "./FluidCanvas";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.15,
    }
  }
};

const letterVariants = {
  hidden: { opacity: 0, y: 35, scale: 0.7, rotate: -10 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    rotate: 0,
    transition: {
      type: "spring",
      damping: 11,
      stiffness: 140
    }
  }
};

export default function LandingPage({ onLogin }) {
  const [visible, setVisible] = useState(false);
  const [mouse, setMouse] = useState({ x: 0, y: 0 });
  const rootRef = useRef(null);

  useEffect(() => { setTimeout(() => setVisible(true), 80); }, []);

  useEffect(() => {
    const onMove = (e) => setMouse({ x: e.clientX / window.innerWidth - 0.5, y: e.clientY / window.innerHeight - 0.5 });
    window.addEventListener("mousemove", onMove);
    return () => { window.removeEventListener("mousemove", onMove); };
  }, []);

  const px = mouse.x * 28;
  const py = mouse.y * 18;

  const foodyLetters = Array.from("Foody");
  const buddyLetters = Array.from("Buddy");

  return (
    <div ref={rootRef} style={{ background: "#111", minHeight: "100vh", position: "relative", overflow: "hidden", display: "flex", flexDirection: "column" }}>

      <style>{`
        @keyframes rise      { from { opacity:0; transform:translateY(40px); } to { opacity:1; transform:translateY(0); } }
        @keyframes fadeIn    { from { opacity:0; } to { opacity:1; } }
        @keyframes shimmer   { from { background-position:-200% 0; } to { background-position:200% 0; } }
        @keyframes lineGrow  { from { width:0; } to { width:100%; } }
        @keyframes marquee   { from { transform:translateX(0); } to { transform:translateX(-50%); } }
        @keyframes floatA    { 0%,100% { transform:translateY(0px) rotate(-6deg); } 50% { transform:translateY(-14px) rotate(-6deg); } }
        @keyframes floatB    { 0%,100% { transform:translateY(0px) rotate(4deg); } 50% { transform:translateY(-10px) rotate(4deg); } }
        @keyframes floatC    { 0%,100% { transform:translateY(0px) rotate(-3deg); } 50% { transform:translateY(-18px) rotate(-3deg); } }
        @keyframes glowPulse { 0%,100% { opacity:0.06; } 50% { opacity:0.13; } }
        @keyframes textGlitch {
          0%,100% { clip-path:inset(0 0 100% 0); transform:translateX(0); }
          10%      { clip-path:inset(10% 0 60% 0); transform:translateX(-4px); }
          20%      { clip-path:inset(40% 0 20% 0); transform:translateX(4px); }
          30%      { clip-path:inset(0 0 100% 0); transform:translateX(0); }
        }

        .lp-root {
          position:relative; z-index:2; flex:1;
          display:flex; flex-direction:column;
          padding:2.5rem 2rem 2.5rem; min-height:100vh;
        }
        @media (min-width:1024px) {
          .lp-root { flex-direction:row; padding:0; }
          .lp-left { flex:1; display:flex; flex-direction:column; justify-content:center; padding:6rem 5rem 6rem 8rem; border-right:1px solid rgba(255,255,255,0.07); }
          .lp-right { width:360px; flex-shrink:0; display:flex !important; flex-direction:column; justify-content:center; padding:6rem 3.5rem; }
          .lp-cta-mobile { display:none !important; }
          .lp-cta-desktop { display:block !important; }
        }
        @media (max-width:1023px) {
          .lp-left { width:100%; }
          .lp-right { display:none; }
          .lp-cta-mobile { display:block; }
          .lp-cta-desktop { display:none; }
        }

        .lp-cta-btn {
          width:100%; background:#D94F00; color:white;
          border:none; border-radius:4px; padding:17px;
          font-family:'Plus Jakarta Sans', sans-serif; font-size:15px; font-weight:500;
          cursor:pointer; letter-spacing:0.1em; text-transform:uppercase;
          transition:background 0.2s, transform 0.15s, box-shadow 0.2s;
          position:relative; overflow:hidden;
        }
        .lp-cta-btn:hover { background:#bf4400; transform:translateY(-2px); box-shadow:0 12px 32px rgba(217,79,0,0.45); }
        .lp-cta-btn:active { transform:scale(0.98); }
        .lp-cta-btn .shimmer-layer {
          position:absolute; inset:0;
          background:linear-gradient(90deg,transparent,rgba(255,255,255,0.12),transparent);
          background-size:200% 100%; animation:shimmer 2.5s infinite;
        }

        .lp-nav-line { width:0; height:1px; background:rgba(255,255,255,0.12); animation:lineGrow 1.2s ease 0.4s forwards; }

        /* Floating food emoji */
        .float-emoji {
          position:absolute; font-size:28px; pointer-events:none; user-select:none;
          opacity:0; transition:opacity 0.8s ease;
          filter:drop-shadow(0 4px 12px rgba(0,0,0,0.4));
        }
        .float-emoji.show { opacity: 0.35; }

        /* Headline word hover */
        .lp-word {
          display:inline-block;
          transition:color 0.2s, transform 0.3s cubic-bezier(0.34,1.56,0.64,1);
          cursor:default;
        }
        .lp-word:hover { color:#D94F00 !important; transform:translateY(-4px) skewX(-3deg); }

        /* Stat cards */
        .lp-stat {
          transition:transform 0.3s cubic-bezier(0.34,1.56,0.64,1), background 0.2s;
          cursor:default; border-radius:8px; padding:8px 12px;
        }
        .lp-stat:hover { transform:translateY(-4px) scale(1.04); background:rgba(255,255,255,0.04); }

        /* Scroll hint */
        @keyframes scrollBounce { 0%,100%{transform:translateY(0);opacity:0.4;} 50%{transform:translateY(6px);opacity:0.9;} }
        .scroll-hint { animation:scrollBounce 1.8s ease-in-out infinite; }
      `}</style>

      <LiquidBackground />
      <FluidCanvas />

      {/* Grain */}

      {/* Mouse-parallax glow */}
      <div style={{ position: "absolute", width: 600, height: 600, borderRadius: "50%", background: "#D94F00", opacity: 0.07, filter: "blur(130px)", top: `calc(-200px + ${py * 2}px)`, right: `calc(-100px + ${px * 2}px)`, pointerEvents: "none", zIndex: 1, transition: "top 0.1s, right 0.1s", animation: "glowPulse 4s ease-in-out infinite" }} />
      <div style={{ position: "absolute", width: 300, height: 300, borderRadius: "50%", background: "#ff8c42", opacity: 0.04, filter: "blur(80px)", bottom: `calc(60px + ${-py}px)`, left: `calc(-60px + ${-px}px)`, pointerEvents: "none", zIndex: 1, transition: "bottom 0.1s, left 0.1s" }} />

      {/* Floating food emojis — parallax */}
      {[
        { emoji: "🍔", top: "15%", right: "8%", anim: "floatA 5s ease-in-out infinite" },
        { emoji: "🌯", top: "55%", right: "14%", anim: "floatB 6s ease-in-out infinite 1s" },
        { emoji: "🥤", top: "30%", right: "3%", anim: "floatC 7s ease-in-out infinite 0.5s" },
        { emoji: "🍕", top: "70%", right: "22%", anim: "floatA 5.5s ease-in-out infinite 2s" },
        { emoji: "🍟", top: "80%", right: "12%", anim: "floatA 5.5s ease-in-out infinite 2s" },
      ].map((f, i) => (
        <div key={i} className={`float-emoji ${visible ? "show" : ""}`} style={{
          top: f.top, right: f.right,
          transform: `translate(${px * (i % 2 === 0 ? 1.5 : -1)}px, ${py * (i % 2 === 0 ? 1 : -1.5)}px)`,
          transition: "transform 0.15s ease-out",
          animation: f.anim,
          zIndex: 1,
        }}>{f.emoji}</div>
      ))}

      <div className="lp-root">

        {/* LEFT */}
        <div className="lp-left" style={{ position: "relative", zIndex: 2 }}>

          {/* Nav row */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "3.5rem", opacity: visible ? 1 : 0, animation: visible ? "fadeIn 0.5s ease 0.1s both" : "none" }}>
            <div style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 12, fontWeight: 500, letterSpacing: "0.12em", textTransform: "uppercase", color: "rgba(255,255,255,0.3)" }}>
              Karunya Institute of Technology and Sciences
            </div>
          </div>

          {/* Brand wordmark with staggered letter-by-letter animation & hover effects */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={visible ? "visible" : "hidden"}
            style={{
              fontFamily: "'Sora', sans-serif",
              fontSize: "clamp(32px, 8vw, 72px)", fontWeight: 900, letterSpacing: "-1px",
              marginBottom: "1rem",
              transform: `translate(${px * 0.4}px, ${py * 0.3}px)`,
              transition: "transform 0.12s ease-out",
              display: "flex",
              flexWrap: "wrap",
            }}
          >
            <div style={{ display: "flex", marginRight: "14px" }}>
              {foodyLetters.map((char, i) => (
                <motion.span
                  key={`foody-${i}`}
                  variants={letterVariants}
                  whileHover={{ y: -8, scale: 1.15, color: "#ff7a2f", transition: { type: "spring", stiffness: 350 } }}
                  style={{ color: "#D94F00", display: "inline-block", cursor: "default" }}
                >
                  {char}
                </motion.span>
              ))}
            </div>
            <div style={{ display: "flex" }}>
              {buddyLetters.map((char, i) => (
                <motion.span
                  key={`buddy-${i}`}
                  variants={letterVariants}
                  whileHover={{ y: -8, scale: 1.15, color: "#ff7a2f", transition: { type: "spring", stiffness: 350 } }}
                  style={{ color: "white", display: "inline-block", cursor: "default" }}
                >
                  {char}
                </motion.span>
              ))}
            </div>
          </motion.div>

          {/* Headline with per-word hover */}
          <div style={{
            fontFamily: "'Sora', sans-serif",
            fontSize: "clamp(38px, 9vw, 82px)", fontWeight: 750,
            lineHeight: 0.95, letterSpacing: "-2px", color: "#fff",
            marginBottom: "1.5rem",
            opacity: visible ? 1 : 0, animation: visible ? "rise 0.8s ease 0.25s both" : "none",
            transform: `translate(${px * 0.6}px, ${py * 0.4}px)`,
            transition: "transform 0.12s ease-out",
          }}>
            <span className="lp-word">Eat</span><br />
            <span className="lp-word" style={{ fontStyle: "italic", color: "#D94F00" }}>Without</span><br />
            <span className="lp-word">Waiting.</span>
          </div>

          {/* Thin rule */}
          <div className="lp-nav-line" style={{ marginBottom: "1.75rem" }} />

          {/* Tagline */}
          <p style={{
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontSize: "clamp(14px,1.1vw,15px)", fontWeight: 300,
            color: "rgba(255,255,255,0.4)", lineHeight: 1.8,
            marginBottom: "2.5rem", letterSpacing: "0.01em", maxWidth: 360,
            opacity: visible ? 1 : 0, animation: visible ? "rise 0.7s ease 0.45s both" : "none",
          }}>
            Pre-order from the canteen. Pay instantly.<br />Collect when ready — zero queue.
          </p>

          {/* Mobile CTA */}
          <div className="lp-cta-mobile" style={{ opacity: visible ? 1 : 0, animation: visible ? "rise 0.7s ease 0.6s both" : "none" }}>
            <button className="lp-cta-btn" onClick={onLogin}>
              <span style={{ position: "relative", zIndex: 1 }}>Get Started →</span>
              <div className="shimmer-layer" />
            </button>
          </div>


        </div>

        {/* RIGHT — desktop only */}
        <div className="lp-right" style={{ position: "relative", zIndex: 2 }}>
          <div className="lp-cta-desktop" style={{ opacity: visible ? 1 : 0, animation: visible ? "rise 0.7s ease 0.5s both" : "none" }}>
            <button className="lp-cta-btn" onClick={onLogin} style={{ marginBottom: 14 }}>
              <span style={{ position: "relative", zIndex: 1 }}>Get Started →</span>
              <div className="shimmer-layer" />
            </button>
            <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 11, color: "rgba(255,255,255,0.18)", textAlign: "center", letterSpacing: "0.06em" }}>
              USE YOUR NAME & PASSWORD TO SIGN IN
            </p>
          </div>
        </div>

      </div>

    </div>
  );
}
